import React, { useContext, useEffect, useState } from "react"
import { BookingContext } from "../../../utils/providers/booking"
import { db, auth } from "../../../utils/firebase"
import firebase from "firebase"

import Textarea from "../../../components/ui/inputs/textarea"
import Button from "../../../components/ui/button/button"
import ActivityFeed from "../../enquiry/_components/activity/feed"
import { BOOKING_QUICK_ACTION_TYPES } from "../../enquiry/_components/activity/types"

/**
 * Notes / Activity tab for the booking page.
 *
 * The legacy `agent_notes` textarea is kept (compact, read/write) for
 * backwards compatibility with bookings that already have notes saved
 * against the document. Going forward all new notes/comms should be
 * captured via the activity feed below.
 *
 * The activity feed has a toggle: by default it shows the booking's own
 * activity, but if the original enquiry's activity was preserved on
 * conversion (under `enquiry_activity`) the user can flip to view it.
 */
export default function Notes() {
	const [notes, setNotes] = useState("")
	const [baseNotes, setBaseNotes] = useState("")
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(true)
	const [view, setView] = useState("BOOKING")
	const [hasEnquiryActivity, setHasEnquiryActivity] = useState(false)

	const { bookingID } = useContext(BookingContext)

	useEffect(() => {
		db.doc(`bookings/${bookingID}`)
			.get()
			.then((bookingDoc) => {
				const { agent_notes } = bookingDoc.data()
				setNotes(agent_notes || "")
				setBaseNotes(agent_notes || "")
				setLoading(false)
			})
	}, [bookingID])

	// Detect whether the booking has any preserved enquiry activity, so we
	// only show the toggle on bookings that were converted from an enquiry
	// (and only after the conversion-time copy ran).
	useEffect(() => {
		const unsub = db
			.collection(`bookings/${bookingID}/enquiry_activity`)
			.limit(1)
			.onSnapshot((snap) => {
				setHasEnquiryActivity(!snap.empty)
			})
		return () => unsub()
	}, [bookingID])

	const saveNotes = async () => {
		const changed =
			(notes !== baseNotes && notes.length > 0) ||
			(baseNotes.length > 0 && notes.length === 0)
		if (!changed) return

		setSaving(true)
		await db.doc(`bookings/${bookingID}`).update({
			agent_notes: notes,
		})
		await db.collection(`bookings/${bookingID}/logs`).add({
			type: "NOTES_UPDATED",
			badge: "INFO",
			message: "The agent notes on the booking were ammended",
			created: firebase.firestore.FieldValue.serverTimestamp(),
			user: auth.currentUser.uid,
			detail: {
				agent_notes: notes,
			},
		})
		setBaseNotes(notes)
		setSaving(false)
	}

	return (
		<div className="booking__notes-activity">
			<section className="booking__legacy-notes">
				<h3 className="booking__legacy-notes-title">Notes</h3>
				<Textarea
					disabled={loading}
					value={notes}
					placeholder="Start typing..."
					onChange={setNotes}
					rows={6}
				/>
				<div className="booking__legacy-notes-actions">
					<Button
						small={true}
						label="Save notes"
						loading={saving}
						loadingText="Saving..."
						onClick={() => saveNotes()}
					/>
				</div>
			</section>

			<div className="booking__notes-divider" aria-hidden="true" />

			{hasEnquiryActivity && (
				<div className="booking__activity-toggle" role="tablist" aria-label="Activity source">
					<button
						type="button"
						role="tab"
						aria-selected={view === "BOOKING"}
						className={`booking__activity-toggle-btn${view === "BOOKING" ? " is-active" : ""}`}
						onClick={() => setView("BOOKING")}>
						Booking activity
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={view === "ENQUIRY"}
						className={`booking__activity-toggle-btn${view === "ENQUIRY" ? " is-active" : ""}`}
						onClick={() => setView("ENQUIRY")}>
						Inquiry activity
					</button>
				</div>
			)}

			{view === "BOOKING" ? (
				<ActivityFeed
					basePath={`bookings/${bookingID}`}
					quickActionTypes={BOOKING_QUICK_ACTION_TYPES}
				/>
			) : (
				<ActivityFeed
					key="enquiry-activity"
					basePath={`bookings/${bookingID}`}
					collectionName="enquiry_activity"
					readOnly={true}
					showAllTypes={true}
					emptyMessage="No activity was logged on the original enquiry."
				/>
			)}
		</div>
	)
}
